import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@calendly/ui/components/dropdown-menu'
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown'
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp'
import { IconX } from '@calendly/ui/components/glyphs/IconX'
import { BodyText, LabelText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { colorIconDestructive, colorTextDestructive, colorTextSecondary } from '@calendly/ui/theme'
import { useEffect, useRef, useState } from 'react'

import { RECAP_PERMISSION_RELATIONS } from 'features/recaps/constants'

import {
  AccessListItemAction,
  permissionSelectMenuContainerCss,
  permissionSelectReadOnlyLabelCss,
  permissionSelectTriggerButtonCss,
} from './styles'

const REMOVE_ACTIONS = {
  REMOVE: 'remove',
  HIDE: 'hide',
}

const getRemoveActionLabel = (removeAction) => {
  if (removeAction === REMOVE_ACTIONS.HIDE) {
    return I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.hide')
  }

  return I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.remove_access')
}

export const getRelationLabel = (relation) => {
  if (relation === RECAP_PERMISSION_RELATIONS.SHARER) {
    return I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.can_view_and_share')
  }

  return I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.can_view')
}

const getTriggerLabel = (value, hiddenWhenNull) => {
  if (hiddenWhenNull && value === null) {
    return I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.hidden')
  }

  return getRelationLabel(value)
}

export const PermissionSelectReadOnly = ({ label, testId }) => (
  <AccessListItemAction>
    <LabelText
      size="large"
      color={colorTextSecondary}
      className={permissionSelectReadOnlyLabelCss}
      data-testid={testId}
    >
      {label}
    </LabelText>
  </AccessListItemAction>
)

const PermissionSelect = ({
  value,
  onChange,
  onRemove,
  testIdPrefix,
  options,
  removeAction = REMOVE_ACTIONS.REMOVE,
  hiddenWhenNull = false, // Show "Hidden" for null org relations only
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const removeOnCloseRef = useRef(false)
  const showRemoveAction = !(hiddenWhenNull && value === null)

  useEffect(() => {
    if (isDropdownOpen || !removeOnCloseRef.current) {
      return undefined
    }

    const frameId = requestAnimationFrame(() => {
      removeOnCloseRef.current = false
      onRemove()
    })

    return () => cancelAnimationFrame(frameId)
  }, [isDropdownOpen, onRemove])

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false)
  }

  const handleToggleDropdown = () => {
    setIsDropdownOpen((open) => !open)
  }

  const handleSelect = (selectedRelation) => {
    setIsDropdownOpen(false)

    if (selectedRelation !== value) {
      onChange(selectedRelation)
    }
  }

  const handleRemove = () => {
    removeOnCloseRef.current = true
    setIsDropdownOpen(false)
  }

  const chevronIcon = isDropdownOpen ? IconChevronUp : IconChevronDown

  return (
    <AccessListItemAction>
      <div className={permissionSelectMenuContainerCss}>
        <DropdownMenu isOpen={isDropdownOpen} onClose={handleCloseDropdown}>
          <DropdownMenuTrigger>
            <Button
              decoration="ghost"
              size="medium"
              className={permissionSelectTriggerButtonCss}
              onClick={handleToggleDropdown}
              aria-label={I18n.t('publisher.meeting_recap.share_modal.access_list.permission_select.aria_label')}
              aria-expanded={isDropdownOpen}
              aria-haspopup="menu"
              data-testid={`${testIdPrefix}-trigger`}
            >
              {getTriggerLabel(value, hiddenWhenNull)}
              <UiIcon icon={chevronIcon} size="small" title="" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent placement="bottom end" data-testid={`${testIdPrefix}-menu`}>
            <DropdownMenuGroup>
              {options.map((relation) => (
                <DropdownMenuItem
                  key={relation}
                  onSelect={() => handleSelect(relation)}
                  data-testid={`${testIdPrefix}-option-${relation}`}
                >
                  <BodyText>{getRelationLabel(relation)}</BodyText>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            {showRemoveAction ? (
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onSelect={handleRemove}
                  data-testid={`${testIdPrefix}-remove`}
                >
                  {removeAction === REMOVE_ACTIONS.REMOVE ? (
                    <UiIcon icon={IconX} size="small" color={colorIconDestructive} title="" />
                  ) : null}
                  <BodyText size="medium" color={colorTextDestructive}>
                    {getRemoveActionLabel(removeAction)}
                  </BodyText>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </AccessListItemAction>
  )
}

const RECIPIENT_RELATION_OPTIONS = [
  RECAP_PERMISSION_RELATIONS.VIEWER,
  RECAP_PERMISSION_RELATIONS.SHARER,
]

export const RecipientPermissionSelect = ({
  uuid,
  value,
  onChange,
  onRemove,
}) => (
  <PermissionSelect
    value={value}
    onChange={onChange}
    onRemove={onRemove}
    testIdPrefix={`access-list-permission-${uuid}`}
    options={RECIPIENT_RELATION_OPTIONS}
  />
)

export const OrganizationPermissionSelect = ({
  value,
  onChange,
  onRemove,
}) => (
  <PermissionSelect
    value={value}
    onChange={onChange}
    onRemove={onRemove}
    testIdPrefix="access-list-organization-permission"
    options={RECIPIENT_RELATION_OPTIONS}
    removeAction={REMOVE_ACTIONS.HIDE}
    hiddenWhenNull
  />
)
